import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, BookOpen, Compass, Eye, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

export const visitorId = () => {
  let v = localStorage.getItem("ep_visitor");
  if (!v) { v = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`; localStorage.setItem("ep_visitor", v); }
  return v;
};

const inline = (text) => text.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith("**") && part.endsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : part));

export function RichText({ text }) {
  const blocks = (text || "").split(/\n{2,}/);
  return (
    <div className="space-y-3 text-slate-700 leading-relaxed" data-testid="glossary-body">
      {blocks.map((b, i) => {
        const lines = b.split("\n").filter((l) => l.trim());
        if (!lines.length) return null;
        const head = lines[0].startsWith("## ") ? <h3 className="text-lg font-bold text-slate-900 pt-2" style={{ fontFamily: "Outfit, sans-serif" }}>{lines[0].slice(3)}</h3> : null;
        const rest = head ? lines.slice(1) : lines;
        const bullets = rest.length && rest.every((l) => l.startsWith("- "));
        return (
          <div key={i}>
            {head}
            {bullets ? <ul className="list-disc pl-5 space-y-1">{rest.map((l, j) => <li key={j}>{inline(l.slice(2))}</li>)}</ul>
              : rest.length > 0 && <p className="whitespace-pre-wrap">{inline(rest.join("\n"))}</p>}
          </div>
        );
      })}
    </div>
  );
}

const TABS = [["TERM", "Emlak Sözlüğü", BookOpen], ["GUIDE", "Kullanım Rehberi", Compass]];

export function GlossaryBrowser({ base }) {
  const [kind, setKind] = useState("TERM");
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState(null);
  const [q, setQ] = useState("");
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => { api.get("/public/glossary/categories").then(({ data }) => setCats(data)).catch(() => {}); }, []);
  useEffect(() => {
    const t = setTimeout(() => {
      api.get("/public/glossary", { params: { kind, category_id: cat || undefined, q: q || undefined } })
        .then(({ data }) => { setItems(data); setErr(""); }).catch((x) => setErr(formatApiError(x.response?.data?.detail)));
    }, 250);
    return () => clearTimeout(t);
  }, [kind, cat, q]);
  const kindCats = useMemo(() => cats.filter((c) => c.kind === kind), [cats, kind]);
  const catName = (id) => cats.find((c) => c.id === id)?.name;

  return (
    <div className="space-y-6" data-testid="glossary-browser">
      <div className="flex flex-wrap gap-2">
        {TABS.map(([k, l, I]) => (
          <button key={k} data-testid={`glossary-tab-${k}`} onClick={() => { setKind(k); setCat(null); }}
            className={`px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 border transition-colors ${kind === k ? "bg-emerald-700 text-white border-emerald-700" : "bg-white text-slate-700 border-slate-200 hover:border-emerald-400"}`}>
            <I className="w-4 h-4" />{l}
          </button>
        ))}
      </div>
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative w-full sm:w-80">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="glossary-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Terim veya rehber ara…" className="pl-9" />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[{ id: null, name: "Tümü" }, ...kindCats].map((c) => (
            <button key={c.id || "all"} data-testid={`glossary-cat-${c.id || "all"}`} onClick={() => setCat(c.id)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${cat === c.id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{c.name}</button>
          ))}
        </div>
      </div>
      {err && <div className="text-sm text-red-700" data-testid="glossary-error">{err}</div>}
      {items && items.length === 0 && <div className="text-slate-500 py-10 text-center" data-testid="glossary-empty">Sonuç bulunamadı.</div>}
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {(items || []).map((e, i) => (
          <Link key={e.id} to={`${base}/${e.slug}`} data-testid={`glossary-item-${e.slug}`} style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}
            className="group rounded-2xl border border-slate-200 bg-white p-5 hover:border-emerald-400 hover:shadow-[0_10px_30px_-12px_rgba(4,120,87,0.35)] transition-[border-color,box-shadow] animate-in fade-in slide-in-from-bottom-2">
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold">{catName(e.category_id) || (e.kind === "TERM" ? "Terim" : "Rehber")}</div>
            <div className="font-bold text-slate-900 mt-1 group-hover:text-emerald-800" style={{ fontFamily: "Outfit, sans-serif" }}>{e.title}</div>
            <p className="text-sm text-slate-500 mt-1 line-clamp-2">{e.summary}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function GlossaryArticle({ slug, base }) {
  const [e, setE] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    setE(null); setErr("");
    api.get(`/public/glossary/${slug}`).then(({ data }) => {
      setE(data);
      api.post(`/public/glossary/${data.id}/view`, { visitor_id: visitorId() })
        .then(({ data: r }) => r.counted && setE((x) => ({ ...x, views: (x.views || 0) + 1 }))).catch(() => {});
    }).catch((x) => setErr(formatApiError(x.response?.data?.detail)));
  }, [slug]);
  return (
    <div className="max-w-3xl space-y-5" data-testid="glossary-article">
      <Link to={base} data-testid="glossary-back-link" className="inline-flex items-center gap-1.5 text-sm text-emerald-800 hover:underline"><ArrowLeft className="w-4 h-4" />Sözlük & Rehber</Link>
      {err && <div className="text-red-700" data-testid="glossary-article-error">{err}</div>}
      {e && (
        <>
          <div>
            <div className="text-xs uppercase tracking-wider text-emerald-700 font-semibold">{e.kind === "TERM" ? "Emlak Sözlüğü" : "Kullanım Rehberi"}</div>
            <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-1" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="glossary-article-title">{e.title}</h1>
            {e.summary && <p className="text-lg text-slate-500 mt-2">{e.summary}</p>}
            <div className="text-xs text-slate-400 mt-2 flex items-center gap-1"><Eye className="w-3.5 h-3.5" /><span data-testid="glossary-article-views">{e.views}</span> görüntülenme</div>
          </div>
          <RichText text={e.body} />
        </>
      )}
    </div>
  );
}
